/**
 * Componente DelegationSelector
 * Selector dropdown para delegaciones físicas
 * @module components/appointments/DelegationSelector
 */

import React from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { useDelegations } from '@/hooks/useDelegations';
import { Label } from '@/components/ui/label';

interface DelegationSelectorProps {
  value?: string;
  onChange: (delegationId: string | undefined) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  showAllOption?: boolean;
  className?: string;
}

export const DelegationSelector: React.FC<DelegationSelectorProps> = ({
  value,
  onChange,
  label = 'Delegación',
  placeholder = 'Todas las delegaciones',
  required = false,
  showAllOption = true,
  className = '',
}) => {
  const { activeDelegations, isLoading, error } = useDelegations();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    onChange(val === '' ? undefined : val);
  };

  // Si hay error del backend, mostrar mensaje y no bloquear la UI
  const hasError = !!error;
  const hasDelegations = activeDelegations && activeDelegations.length > 0;

  return (
    <div className={className}>
      {label && (
        <Label htmlFor="delegation-selector" className="mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <div className="relative">
        <select
          id="delegation-selector"
          value={value || ''}
          onChange={handleChange}
          disabled={isLoading || hasError}
          required={required}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {showAllOption && <option value="">{placeholder}</option>}
          {!showAllOption && !value && (
            <option value="" disabled>
              Seleccionar delegación...
            </option>
          )}
          {isLoading && (
            <option value="" disabled>
              Cargando delegaciones...
            </option>
          )}
          {hasError && (
            <option value="" disabled>
              Error al cargar delegaciones
            </option>
          )}
          {!isLoading && !hasError && !hasDelegations && (
            <option value="" disabled>
              No hay delegaciones disponibles
            </option>
          )}
          {activeDelegations?.map((delegation) => (
            <option key={delegation.id} value={delegation.id}>
              {delegation.name} - {delegation.city}
            </option>
          ))}
        </select>
        <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-600">
          ⚠️ No se pudieron cargar las delegaciones. Contacta con soporte.
        </p>
      )}
    </div>
  );
};
